import type { ExactInput } from './exactInput'
import { gql, type Client, type RequestPolicy } from 'urql'
import type { TypedDocumentNode } from '@urql/core'
import type { ResultOf, VariablesOf, TypedDocumentNode as TypedDocument } from '@graphql-typed-document-node/core'
import type { DocumentNode } from 'graphql'
import type { Query } from 'app/generated/graphql'
import { MODEL_FIELDS } from 'app/generated/modelFields'
import { client } from './urqlClient'

// The generated `Query` type maps every entity name to its precise return type
// (e.g. `contact?: Maybe<Contact>`, `contacts: ContactListResult`). We use it to
// infer helper return types from the `entityName` string literal, so callers get
// field-checking against the generated model without writing a type argument.
// Any query field is accepted; the inference helpers below degrade gracefully
// for fields that don't fit the by-id / list shape.
type EntityName = keyof Query

// Unwrap `Maybe<X>` (generated as `X | undefined`) to `X` for by-id returns.
type OneResult<K extends EntityName> = NonNullable<Query[K]>

// Element type of the `hits` array on a `*ListResult` return type. We match against
// `ReadonlyArray<infer H>` rather than `Array<infer H>` so the conditional accepts
// both mutable and readonly array types: the generated schema declares `hits: Array<X>`,
// and `Array<T>` structurally extends `ReadonlyArray<T>`, so this matches either form.
type ListHit<K extends EntityName> = NonNullable<Query[K]> extends { hits: ReadonlyArray<infer H> } ? H : unknown

type GqlListParams = {
  search?: string
  pagination?: { page?: number; limit?: number }
  sort?: { field: string; direction: string }
}

type GqlFetchListOptions = {
  requestPolicy?: RequestPolicy
  client?: Client
  /**
   * An empty result and a failed read are different answers, and a caller that acts on
   * "zero" must be able to tell them apart. When true and the read errors, throws instead
   * of returning the `{ hits: [], total: 0, page: 0 }` fallback. Off by default so every
   * existing caller keeps today's behavior.
   */
  throwOnError?: boolean
  /**
   * Same flag `gqlQuery` forwards to the request context (see `GqlOperationOptions` above):
   * a caller with its own error message for a failed read sets this so the global urql
   * exchange does not toast the raw error on top of it. It already flows through to the
   * underlying `gqlQuery` call below; this was only missing from the type.
   */
  suppressErrorToast?: boolean
}

/**
 * Non-hook GraphQL client for use outside React components.
 * Use this in Lazy* cache functions, table columns, and other non-React contexts.
 * React components should use store hooks instead.
 */

type GqlResult<T> = { data?: T; error?: { message: string; code?: string } }
// `client` routes to the ops endpoint (opsClient) when an ops-only resolver is
// queried; it defaults to the public /graphql client.
type GqlOperationOptions = {
  requestPolicy?: RequestPolicy
  suppressErrorToast?: boolean
  client?: Client
}

// Operations are typically defined once at module scope, via either a `gql`
// tagged template (DocumentNode) or a plain string literal. Accept both so
// callers can use whichever shape fits the surrounding code without having to
// hand-roll a conversion.
type GqlInput = string | DocumentNode | TypedDocumentNode<unknown, Record<string, unknown>>

const toDocument = (input: GqlInput): DocumentNode => (typeof input === `string` ? gql(input) : input)

// Typed overload: when a `graphql()` document is passed, infer the result and
// variable types from the `TypedDocumentNode`, so callers read `result.data`
// against the exact selection set with no cast. The string/DocumentNode overload
// below keeps the untyped `<T>` path for legacy `gql` and inline usage.
export async function gqlQuery<Data, Variables>(
  query: TypedDocument<Data, Variables>,
  variables: Variables,
  options?: GqlOperationOptions,
): Promise<GqlResult<Data>>
export async function gqlQuery<T = unknown>(
  query: GqlInput,
  variables?: Record<string, unknown>,
  options?: GqlOperationOptions,
): Promise<GqlResult<T>>
export async function gqlQuery<T = unknown>(
  query: GqlInput,
  variables?: Record<string, unknown>,
  options?: GqlOperationOptions,
): Promise<GqlResult<T>> {
  const result = await (options?.client ?? client)
    .query(
      toDocument(query),
      variables ?? {},
      options ? { requestPolicy: options.requestPolicy, suppressErrorToast: options.suppressErrorToast } : undefined,
    )
    .toPromise()
  const code = result.error?.graphQLErrors[0]?.extensions.code
  return {
    data: result.data as T | undefined,
    error: result.error
      ? { message: result.error.message, code: typeof code === `string` ? code : undefined }
      : undefined,
  }
}

export async function gqlMutation<
  Document extends TypedDocument<any, any>,
  Actual extends VariablesOf<Document> = VariablesOf<Document>,
>(
  mutation: Document & ('__apiType' extends keyof Document ? unknown : never),
  ...args: {} extends VariablesOf<Document>
    ? [variables?: Actual & ExactInput<Actual, VariablesOf<Document>>, options?: GqlOperationOptions]
    : [variables: Actual & ExactInput<Actual, VariablesOf<Document>>, options?: GqlOperationOptions]
): Promise<GqlResult<ResultOf<Document>>> {
  const [variables, options] = args
  const result = await (options?.client ?? client)
    .mutation<ResultOf<Document>, VariablesOf<Document>>(
      mutation,
      variables ?? ({} as VariablesOf<Document>),
      options ? { requestPolicy: options.requestPolicy, suppressErrorToast: options.suppressErrorToast } : undefined,
    )
    .toPromise()
  const code = result.error?.graphQLErrors[0]?.extensions.code
  return {
    data: result.data,
    error: result.error
      ? { message: result.error.message, code: typeof code === `string` ? code : undefined }
      : undefined,
  }
}

/**
 * Fetch a single entity by ID. Returns the entity or undefined.
 *
 * When `fields` is omitted, the helper uses `MODEL_FIELDS[entityName]` so stores get
 * every exposed field (per AGENTS.md guidance). When `fields` is explicitly passed,
 * the caller's value wins — important for any caller that needs to sidestep a broken
 * resolver in the wide model (e.g. the currently-throwing `user.role` field).
 *
 * Preferred form: `gqlFetchOne('contact', id)` — the return type is inferred from the
 * generated `Query` type, so accessing a property not on the model is a compile error.
 *
 * Legacy form: `gqlFetchOne<SomeShape>('contact', id)` — an explicit type overrides
 * inference. Prefer generated model types over hand-written shapes (AGENTS.md).
 */
export function gqlFetchOne<K extends EntityName>(
  entityName: K,
  id: string,
  fields?: string,
  options?: GqlOperationOptions,
): Promise<OneResult<K> | undefined>
export function gqlFetchOne<T>(
  entityName: string,
  id: string,
  fields?: string,
  options?: GqlOperationOptions,
): Promise<T | undefined>
export async function gqlFetchOne(
  entityName: string,
  id: string,
  fields?: string,
  options?: GqlOperationOptions,
): Promise<unknown> {
  const modelFields = fields ?? MODEL_FIELDS[entityName] ?? 'id'
  const opName = entityName.charAt(0).toUpperCase() + entityName.slice(1)
  const query = `query ${opName}ById($id: ID!) { ${entityName}(id: $id) { ${modelFields} } }`
  const result = await gqlQuery<Record<string, unknown>>(query, { id }, options)
  return result.data?.[entityName]
}

/**
 * Fetch a list of entities. Returns { hits, total, page }. Same fields-precedence as gqlFetchOne.
 *
 * Preferred form: `gqlFetchList('contacts')` — the `hits` element type is inferred from
 * the generated `*ListResult.hits` array. Legacy form: `gqlFetchList<Shape>('contacts')`
 * overrides inference; prefer generated model types over hand-written shapes.
 */
export function gqlFetchList<K extends EntityName>(
  entityName: K,
  params?: GqlListParams,
  fields?: string,
  options?: GqlFetchListOptions,
): Promise<{ hits: ListHit<K>[]; total: number; page: number }>
export function gqlFetchList<T>(
  entityName: string,
  params?: GqlListParams,
  fields?: string,
  options?: GqlFetchListOptions,
): Promise<{ hits: T[]; total: number; page: number }>
export async function gqlFetchList(
  entityName: string,
  params?: GqlListParams,
  fields?: string,
  options?: GqlFetchListOptions,
): Promise<{ hits: unknown[]; total: number; page: number }> {
  const modelFields = fields ?? MODEL_FIELDS[entityName] ?? 'id'
  const opName = entityName.charAt(0).toUpperCase() + entityName.slice(1)
  const query = `query ${opName}List($search: String, $pagination: PaginationInput, $sort: SortInput) { ${entityName}(search: $search, pagination: $pagination, sort: $sort) { hits { ${modelFields} } total page } }`
  // Normalize sort direction to uppercase — the GraphQL SortDirection enum only accepts ASC/DESC,
  // but callers (URL params, table components) often pass lowercase "asc"/"desc".
  const variables = params?.sort
    ? { ...params, sort: { ...params.sort, direction: params.sort.direction.toUpperCase() } }
    : params
  const result = await gqlQuery<Record<string, { hits: unknown[]; total: number; page: number }>>(
    query,
    variables,
    options,
  )
  if (options?.throwOnError && result.error) throw new Error(result.error.message)
  return result.data?.[entityName] ?? { hits: [], total: 0, page: 0 }
}
